import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { gbp } from "../lib/api";
import { useCart } from "../context/CartContext";
import { PricingToggle } from "./PricingToggle";

export const ProductCard = ({ product, index = 0 }) => {
  const [plan, setPlan] = useState("onetime");
  const { addItem } = useCart();
  const price = plan === "subscribe" ? product.price_subscribe : product.price_onetime;

  return (
    <motion.div
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="group relative flex flex-col rounded-[1.5rem] border border-tan bg-offwhite overflow-hidden"
    >
      <Link
        to={`/product/${product.slug}`}
        data-testid={`product-image-link-${product.slug}`}
        className="relative block bg-white overflow-hidden"
      >
        <div className="aspect-[4/5] overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-contain p-2 transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
          />
        </div>
        <span className="absolute top-4 left-4 bg-cream/90 backdrop-blur text-ink text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full">
          {product.badge}
        </span>
        <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-forest text-cream flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
        </span>
      </Link>

      <div className="p-6 md:p-7 flex flex-col gap-5 flex-1">
        <div>
          <p className="font-serif italic text-faded text-sm mb-1">{product.ritual} ritual</p>
          <h3 className="font-sans font-extrabold text-xl md:text-2xl tracking-tight leading-tight text-ink">
            {product.name}
          </h3>
          <p className="text-sm text-faded mt-1">{product.flavour} · {product.count}</p>
        </div>

        <PricingToggle plan={plan} onChange={setPlan} testid={`toggle-${product.slug}`} />

        <div className="mt-auto flex items-end justify-between gap-4 pt-2">
          <div className="flex flex-col">
            <span data-testid={`price-${product.slug}`} className="font-sans font-black text-2xl text-ink">
              {gbp(price)}
            </span>
            {plan === "subscribe" && (
              <span className="text-xs text-faded line-through">{gbp(product.price_onetime)}</span>
            )}
          </div>
          <button
            data-testid={`add-to-bag-${product.slug}`}
            onClick={() => {
              addItem(product, plan, 1);
              toast.success(`${product.name} added to your bag`);
            }}
            className="group/btn flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-3 text-xs font-bold tracking-widest uppercase transition-colors duration-300 hover:bg-forest-deep"
          >
            Add
            <Plus className="w-4 h-4 transition-transform duration-300 group-hover/btn:rotate-90" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
